define('home/models/Forum',[
    'jquery',
    'underscore',
    'backbone',
    'require',

    'core/utils/html',
    'core/models/Forum',

    'core/api',
    'core/utils',
], function (
    $,
    _,
    Backbone,
    require,

    html,
    CoreForum,

    api,
    utils
) {
    'use strict';

    var Forum = CoreForum.extend({
        // Removes protocol, trailing slash to make a user-friendly
        // displayable url that goes to this forum's website
        getFriendlyUrl: function () {
            var friendlyUrl = utils.getDomain(this.get('url'));
            return friendlyUrl.indexOf('disqus.com') > -1 ? 'disqus.com' : friendlyUrl;
        },

        initialize: function (attrs, options) {
            if (!this.get('id'))
                throw new Error('Forum must be initialized with an id');

            CoreForum.prototype.initialize.call(this, attrs, options);

            this.fetch = _.memoize(_.bind(this.fetch, this));

            this.buildChannel(options);
            this.listenTo(this, 'change:channel', this.updateChannel);

            // Event specifically for when a user action cause the isFollowing attr to
            // toggle its value. Cannot use change:isFollowing because it is triggered
            // on sync events.
            this.listenTo(this, 'toggled:isFollowing', this.handleFollowingToggled);
        },

        /*
         * Initialize the related Channel model, if given channel details,
         * either in the given options, or in this Forum's attributes
         */
        buildChannel: function (options) {
            if (this.channel)
                return;

            var channel = options && options.channel || this.get('channel');
            if (!channel)
                return;

            // Prefer to pass primaryForum as an attribute, rather than an option,
            // because if the Channel model already exists, UniqueModel will only
            // update its attributes and will ignore the given options.
            if ($.isPlainObject(channel)) {
                channel.primaryForum = this;
            } else {
                // channel may be just an id, so pass primaryForum as an option
                options = _.defaults({
                    primaryForum: this,
                }, options);
            }

            var Channel = require('home/models/Channel');
            this.initializeRelated('channel',
                options,
                Channel);
        },

        /*
         * Handle getting Channel details in this Forum's attributes.
         * If necessary, initialize a new Channel or update an existing one.
         */
        updateChannel: function () {
            var channelAttrs = this.get('channel');
            this.unset('channel');

            if (!channelAttrs)
                return;

            if (!this.channel)
                this.buildChannel({ channel: channelAttrs });
            else if (this.channel.shouldFetch() && _.isObject(channelAttrs))
                this.channel.set(channelAttrs);
        },

        url: function () {
            return api.getURL('forums/details?forum=' + this.get('id'));
        },

        parse: function (response) {
            return response.response || response;
        },

        /*
         * Returns true if it looks like this forum has not been filled
         * with data yet.
         * @returns {boolean}
         */
        shouldFetch: function () {
            return !this.get('name');
        },

        shouldFetchWithAttachments: function (attachments) {
            return this.shouldFetch() || _.some(attachments, function (attachmentName) {
                return !this.has(attachmentName);
            }, this);
        },

        fetchWithAttachments: function (attachments) {
            this.fetch({
                data: {
                    attach: attachments,
                },
            });
        },

        hasFavicon: function () {
            var favicon = this.getFavicon();
            return favicon && !favicon.match(/favicon-default\.png/);
        },

        getFavicon: function () {
            var forumFavicon = (this.get('favicon') || {}).cache;
            return this.channel && this.channel.get('options').favicon || forumFavicon;
        },

        hasGuidelines: function () {
            // Empty guidelines are returned as empty <p> tags. Check text length
            // to determine if there are guidelines to show.
            return Boolean($(this.get('guidelines')).text().length);
        },

        /*
         * Handles updating counts and triggering events when a user
         * follows/unfollows this forum (or related channel).
         *
         * IMPORTANT: Should only be called when a user action resulted in
         * the following state changing, not when the following state is set
         * via a sync.
         */
        handleFollowingToggled: function () {
            var eventName, followersDelta;
            if (this.get('isFollowing')) {
                eventName = 'followed';
                followersDelta = 1;
            } else {
                eventName = 'unfollowed';
                followersDelta = -1;
            }

            var Session = require('home/models/Session');
            var sessionUser = Session.get().user;

            sessionUser.trigger(eventName + ':forum', this);

            if (this.has('numFollowers'))
                this.set('numFollowers', this.get('numFollowers') + followersDelta);
        },

        toJSON: function () {
            var existing = Backbone.Model.prototype.toJSON.apply(this, arguments);
            var data = _.defaults({
                forumRoute: this.getForumRoute(),
                faviconImage: this.getFavicon(),
            }, existing);

            // Include a subset of Channel information because we will otherwise
            // cause a circular dependency loop by including the full Channel JSON
            if (this.channel)
                data.channelInfo = _.pick(this.channel.attributes, ['name', 'slug', 'options']);

            return data;
        },

        getPlainDescription: function () {
            var description = this.get('description');
            if (!description)
                return;

            return html.stripTags(description);
        },

        getForumRoute: function () {
            var route = this.channel ? [
                'channel',
                this.channel.id,
            ] : [
                'forum',
                this.id,
            ];

            return route.join('/');
        },
    });

    return Forum;
});

